import React from 'react';
import {omit} from 'lodash';

const defaultOptions = {
  useLastComponentAsChildrenWrapper: false
};

/**
 * HoC for wrapping a list of components in a div. Places children right before the closing
 * div tag by default.
 * @param {...React.Component} - Components to be wrapped
 * @param {Object} [options] - Optional options object
 * @param {boolean} [options.useLastComponentAsChildrenWrapper] - If true, children will be passed to the last component
 *   provided, instead of being placed before the closing div tag
 * @return {React.Component}
 */
export function makeSiblings(...args) {
  const lastIndex = args.length - 1;
  const lastArg = args[lastIndex];
  const hasOptionsArg = typeof lastArg === 'object';
  const optionsArg = hasOptionsArg ? {...defaultOptions, ...lastArg} : defaultOptions;
  const components = hasOptionsArg ? args.slice(0, lastIndex) : args;

  return class SiblingComponent extends React.Component {
    mapComponents = (Component, i, arr) => {
      if (!React.Component.isPrototypeOf(Component)) {
        return null;
      }
      const shouldPassChildren =
        optionsArg.useLastComponentAsChildrenWrapper && arr.length === i + 1;
      const passthroughProps = shouldPassChildren ? this.props : omit(this.props, 'children');
      return <Component key={i} {...passthroughProps} />;
    };

    getChildContent = () => {
      const {children, ...passthroughProps} = this.props;
      return !children || optionsArg.useLastComponentAsChildrenWrapper
        ? null
        : React.cloneElement(children, passthroughProps);
    };

    render() {
      return (
        <div>
          {components.map(this.mapComponents)}
          {this.getChildContent()}
        </div>
      );
    }
  };
}
